import { cn } from '@cobliteam/colib-ui/cn';
import Icon from '@cobliteam/colib-ui/Icon';
import Spinner from '@cobliteam/colib-ui/Spinner';
import { useTranslation } from '@cobliteam/i18n';
import type { ReactNode } from 'react';
import { FixedSizeList, type ListChildComponentProps } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';

interface SelectionListProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => ReactNode;
  viewportHeight?: number;
  rowHeight?: number;
  loading?: boolean;
  fetchingMore?: boolean;
  hasMore?: boolean;
  loadMore?: () => void;
  empty?: ReactNode;
  loadingContent?: ReactNode;
  className?: string;
}

export const SelectionListLoading = () => {
  const { t } = useTranslation('common');
  return (
    <div className='flex h-52 items-center justify-center px-5' role='status' aria-label={t('messages.loading')}>
      <Spinner aria-hidden />
    </div>
  );
};

export const SelectionListEmpty = ({ label, iconName = 'clipboard-list', className }: { label?: string; iconName?: 'truck' | 'clipboard-list'; className?: string }) => {
  const { t } = useTranslation('common');
  return (
    <div className={cn('flex h-52 flex-col items-center justify-center text-muted-foreground', className)}>
      <Icon name={iconName} aria-hidden />
      <p>{label ?? t('messages.no_data')}</p>
    </div>
  );
};

interface SelectionRowData<T> {
  items: T[];
  renderItem: (item: T, index: number) => ReactNode;
  loadingContent: ReactNode;
}

const SelectionListRow = <T,>({ index, style, data }: ListChildComponentProps<SelectionRowData<T>>) => (
  <div style={style}>{index < data.items.length ? data.renderItem(data.items[index], index) : data.loadingContent}</div>
);

const SelectionList = <T,>({
  items,
  renderItem,
  viewportHeight = 300,
  rowHeight = 24,
  loading,
  fetchingMore,
  hasMore,
  loadMore,
  empty,
  loadingContent,
  className,
}: SelectionListProps<T>) => {
  const loadingRow = loadingContent ?? <SelectionListLoading />;
  if (loading) return loadingRow;
  if (!items.length) return empty ?? <SelectionListEmpty />;

  const count = items.length + (hasMore ? 1 : 0);
  const isLoaded = (index: number) => !hasMore || index < items.length;
  return (
    <InfiniteLoader isItemLoaded={isLoaded} itemCount={count} loadMoreItems={fetchingMore ? () => {} : () => loadMore?.()}>
      {({ onItemsRendered, ref }) => (
        <FixedSizeList<SelectionRowData<T>>
          ref={ref}
          className={cn('scrollBar', className)}
          height={viewportHeight}
          width='100%'
          itemCount={count}
          itemSize={rowHeight}
          onItemsRendered={onItemsRendered}
          itemData={{ items, renderItem, loadingContent: loadingRow }}
        >
          {SelectionListRow<T>}
        </FixedSizeList>
      )}
    </InfiniteLoader>
  );
};

export default SelectionList;
